import { PhaseTypeEnum, PhaseStructureTypeEnum } from '@vatos-pas/common';

// Bump phases constant
export const BUMP_PHASES = ['Bump1', 'Bump2', 'Bump3', 'Bump4', 'Bump5'];

export const TRIM_OPTION_PHASES = [
  PhaseTypeEnum.Trim1,
  PhaseTypeEnum.DrywallTouchUp1,
  PhaseTypeEnum.InteriorPaint,
  PhaseTypeEnum.Trim2,
];

export const isTrimOptionPhase = (phaseType: PhaseTypeEnum): boolean =>
  TRIM_OPTION_PHASES.includes(phaseType);

// Phase configurations for each job type
export const PHASE_CONFIGS = {
  [PhaseStructureTypeEnum.Drywall]: [
    PhaseTypeEnum.Garage,
    PhaseTypeEnum.Hanging,
    PhaseTypeEnum.Finishing,
    PhaseTypeEnum.Spraying,
    PhaseTypeEnum.Bump1,
    PhaseTypeEnum.Bump2,
    PhaseTypeEnum.Bump3,
    PhaseTypeEnum.Bump4,
    PhaseTypeEnum.Bump5,
  ],
  [PhaseStructureTypeEnum.DrywallAndPaint]: [
    PhaseTypeEnum.Garage,
    PhaseTypeEnum.Hang,
    PhaseTypeEnum.Finish,
    PhaseTypeEnum.Prime,
    PhaseTypeEnum.Trim1,
    PhaseTypeEnum.ExteriorOne,
    PhaseTypeEnum.DrywallTouchUp1,
    PhaseTypeEnum.InteriorPaint,
    PhaseTypeEnum.Trim2,
    PhaseTypeEnum.DrywallTouchUp2,
    PhaseTypeEnum.FinalPaint,
    PhaseTypeEnum.DrywallTouchUp3,
    PhaseTypeEnum.CMBluetape,
    PhaseTypeEnum.FinalBluetape,
    PhaseTypeEnum.ExteriorTwo,
  ],
};

// Modal component types
export type ModalComponentType =
  | 'contractor'
  | 'completionDate'
  | 'boardType'
  | 'boardQuantity'
  | 'options'
  | 'optionsConfirmation';

// Modal configuration interface
export interface ModalConfig {
  modalType: 'request' | 'log';
  components: ModalComponentType[];
  mondayResource: boolean;
  mondayResourcePosition?: 'above-options' | 'default';
  warningMessage?: string;
}

// Request Resource Modal configurations for each phase
export const MODAL_CONFIGS: Record<PhaseTypeEnum, ModalConfig> = {
  // Materials phase (no modal)
  [PhaseTypeEnum.Materials]: {
    modalType: 'request',
    components: [],
    mondayResource: false,
  },

  // Garage phase (same for both job types)
  [PhaseTypeEnum.Garage]: {
    modalType: 'log',
    components: ['contractor', 'completionDate', 'boardType', 'boardQuantity'],
    mondayResource: false,
  },

  // Drywall job type phases
  [PhaseTypeEnum.Hanging]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Finishing]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Spraying]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Bump1]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Bump2]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Bump3]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Bump4]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Bump5]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: true,
  },

  // DrywallAndPaint job type phases
  [PhaseTypeEnum.Hang]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Finish]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.Prime]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.DrywallTouchUp1]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: false,
  },
  [PhaseTypeEnum.Trim1]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.InteriorPaint]: {
    modalType: 'request',
    components: ['contractor', 'options'],
    mondayResource: true,
    mondayResourcePosition: 'above-options',
  },
  [PhaseTypeEnum.DrywallTouchUp2]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: false,
  },
  [PhaseTypeEnum.Trim2]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.FinalPaint]: {
    modalType: 'request',
    components: ['contractor', 'options'],
    mondayResource: true,
    mondayResourcePosition: 'above-options',
  },
  [PhaseTypeEnum.DrywallTouchUp3]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: false,
  },
  [PhaseTypeEnum.CMBluetape]: {
    modalType: 'log',
    components: ['contractor', 'completionDate', 'options'],
    mondayResource: false,
    warningMessage:
      'Please ensure that all interior and/or exterior options have been finalized during this phase!',
  },
  [PhaseTypeEnum.FinalBluetape]: {
    modalType: 'log',
    components: ['contractor', 'completionDate'],
    mondayResource: false,
  },
  [PhaseTypeEnum.ExteriorOne]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
  [PhaseTypeEnum.ExteriorTwo]: {
    modalType: 'request',
    components: ['contractor'],
    mondayResource: true,
  },
};

// Utility functions
export const getPhasesForJobType = (
  phaseStructureType: PhaseStructureTypeEnum,
): PhaseTypeEnum[] => {
  return PHASE_CONFIGS[phaseStructureType] || [];
};

export const getModalConfig = (phaseType: PhaseTypeEnum): ModalConfig => {
  return (
    MODAL_CONFIGS[phaseType] || {
      modalType: 'request',
      components: ['contractor'],
      mondayResource: true,
    }
  );
};

export const shouldShowMondayResource = (): boolean => {
  return new Date().getDay() === 5; // Friday
};

export const getPhaseDisplayName = (phaseType: PhaseTypeEnum): string => {
  // Convert enum values to display names
  const displayNames: Record<PhaseTypeEnum, string> = {
    [PhaseTypeEnum.Materials]: 'Materials',
    [PhaseTypeEnum.Garage]: 'Garage',
    [PhaseTypeEnum.Hanging]: 'Hanging',
    [PhaseTypeEnum.Finishing]: 'Finishing',
    [PhaseTypeEnum.Spraying]: 'Spraying',
    [PhaseTypeEnum.Bump1]: 'Bump 1',
    [PhaseTypeEnum.Bump2]: 'Bump 2',
    [PhaseTypeEnum.Bump3]: 'Bump 3',
    [PhaseTypeEnum.Bump4]: 'Bump 4',
    [PhaseTypeEnum.Bump5]: 'Bump 5',
    [PhaseTypeEnum.Hang]: 'Hang',
    [PhaseTypeEnum.Finish]: 'Finish',
    [PhaseTypeEnum.Prime]: 'Prime',
    [PhaseTypeEnum.DrywallTouchUp1]: 'Drywall Touch Up 1',
    [PhaseTypeEnum.Trim1]: 'Trim 1',
    [PhaseTypeEnum.InteriorPaint]: 'Interior Paint',
    [PhaseTypeEnum.DrywallTouchUp2]: 'Drywall Touch Up 2',
    [PhaseTypeEnum.Trim2]: 'Trim 2',
    [PhaseTypeEnum.FinalPaint]: 'Final Paint',
    [PhaseTypeEnum.DrywallTouchUp3]: 'Drywall Touch Up 3',
    [PhaseTypeEnum.CMBluetape]: 'CM Bluetape',
    [PhaseTypeEnum.FinalBluetape]: 'Final Bluetape',
    [PhaseTypeEnum.ExteriorOne]: 'Exterior One',
    [PhaseTypeEnum.ExteriorTwo]: 'Exterior Two',
  };

  return displayNames[phaseType] || phaseType;
};
